import { EyeIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import ShareButtons from 'components/ShareButtons';
import { useGameState, useHeadline } from 'contexts/HeadlineContext';
import { useSettings } from 'contexts/SettingsContext';
import { useToast } from 'contexts/ToastContext';
import { recordGameCompleted } from 'lib/api';
import {
  checkAnswer,
  countWrongGuesses,
  getHintPenalty,
  getHintPrompt,
  getWrongGuesses,
  isHintAvailable,
} from 'lib/game';
import { incrementStat, saveResult } from 'lib/storage';
import { toastWrongAnswer } from 'lib/ui';
import { isStandard } from 'lib/utils';
import React, { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { Hint } from 'types';
import AnswerWheel, { PLACEHOLDER_VALUE } from './AnswerWheel';
import ExpertInput from './ExpertInput';
import HeadlineDisplay from './HeadlineDisplay';
import HintsAndGuesses from './HintsAndGuesses';
import Suggestions from './Suggestions';

const GameContainer: React.FC = () => {
  const [gameState, setGameState] = useGameState();
  const headline = useHeadline();
  const { expertMode } = useSettings().settings;
  const [inputValue, setInputValue] = useState('');
  const [displayGuess, setDisplayGuess] = useState('');
  const [, startTransition] = useTransition();
  const expertInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleSetGuess = useCallback((val: string) => {
    setInputValue(val);
    startTransition(() => {
      setDisplayGuess(val);
    });
  }, []);

  useEffect(() => {
    if (gameState.completedAt) {
      const hasParam =
        window.location.search.includes('id=') || window.location.search.includes('game=');
      // If it's like domain.com/[game]
      const hasUrlSegment = window.location.pathname.split('/').length > 1;
      if (hasParam || hasUrlSegment) {
        toast('Great job!', 'success');
      } else {
        // We are on today's game. If it was completed in the last ~5 seconds, it means we just completed it
        // and should say "Nice". If it's older than that, say "Great job today" as a reminder the game is already won.
        const isOlderThanFiveSeconds = Date.now() - gameState.completedAt! > 5000;
        if (gameState.completedAt)
          toast(isOlderThanFiveSeconds ? 'Great job today!' : 'Nice!', 'success');
      }
    }
  }, [gameState.completedAt, toast]);

  const handleGuess = useCallback(() => {
    if (!inputValue) return;
    const isCorrect = checkAnswer(inputValue, headline.correctAnswer);

    if (isCorrect) {
      handleSetGuess(headline.correctAnswer);
      setGameState(prev => ({ ...prev, correct: true, completedAt: Date.now() }));
      incrementStat('totalPlays');
      if (!countWrongGuesses(gameState)) {
        incrementStat('firstGuessCorrectCount');
      }

      if (isStandard(headline)) {
        saveResult(headline, new Date(), countWrongGuesses(gameState), expertMode);
        recordGameCompleted(headline.id, getWrongGuesses(gameState));
      }
    } else {
      expertInputRef.current?.focus();

      if (getWrongGuesses(gameState).find(g => g === inputValue)) {
        toast('Already guessed!', 'warning');
        return;
      } else {
        toastWrongAnswer(toast);
      }
      incrementStat('totalIncorrectGuesses');
      setGameState(({ actions = [], ...rest }) => ({
        ...rest,
        actions: [...actions, inputValue],
      }));
      if (expertMode) {
        handleSetGuess('');
      }
    }
  }, [inputValue, gameState, headline, setGameState, expertMode, toast, handleSetGuess]);

  const handleGuessRef = useRef(handleGuess);
  handleGuessRef.current = handleGuess;

  useEffect(() => {
    if (!gameState.completedAt) {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleGuessRef.current();
        }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [gameState.completedAt]);

  const onHintClick = useCallback(
    (hintType: Hint) => {
      if (window.confirm(getHintPrompt(gameState, headline.correctAnswer, hintType))) {
        setGameState(g => ({ ...g, actions: [...(g.actions || []), hintType] }));
        toast('Hint revealed!', 'info');
      }
    },
    [gameState, headline.correctAnswer, setGameState, toast]
  );

  useEffect(() => {
    if (expertMode) {
      handleSetGuess('');
    }
  }, [expertMode, handleSetGuess]);

  const renderHintButton = (hintType: Hint) => {
    return (
      <div className="flex flex-col items-center">
        {/* To center the button vertically */}
        <span className="text-xs">&nbsp;</span>
        <button
          className="mb-1 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title={hintType === Hint.CHAR ? 'Reveal a letter' : 'Get a hint'}
          onClick={() => onHintClick(hintType)}
          disabled={!isHintAvailable(expertMode, gameState, headline, hintType)}
        >
          {hintType === Hint.CHAR ? (
            <EyeIcon className="w-5 h-5" />
          ) : (
            <LightBulbIcon className="w-5 h-5" />
          )}
        </button>
        <span className="text-xs text-gray-500">-{getHintPenalty(headline, hintType)} pts</span>
      </div>
    );
  };

  return (
    <>
      <section className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-4 sm:p-8 w-full max-w-4xl mx-auto">
        <HeadlineDisplay currentGuess={displayGuess} isGameOver={!!gameState.completedAt} />
        <div className="flex flex-col items-center w-full">
          <div className="w-full flex justify-center">
            {gameState.completedAt ? null : expertMode ? (
              <ExpertInput
                ref={expertInputRef}
                onSetGuess={handleSetGuess}
                currentGuess={inputValue}
              />
            ) : (
              <AnswerWheel
                choices={isStandard(headline) ? headline.possibleAnswers : []}
                onSetGuess={handleSetGuess}
              />
            )}
          </div>

          {gameState.completedAt ? (
            <div className="w-full">
              <ShareButtons />
            </div>
          ) : (
            <div className="grid grid-cols-3 items-center gap-4 mt-4 w-full">
              {expertMode ? renderHintButton(Hint.CHAR) : <div />}

              <button
                className="justify-self-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                onClick={handleGuess}
                disabled={!inputValue || inputValue === PLACEHOLDER_VALUE}
              >
                Submit
              </button>

              {renderHintButton(Hint.CLUE)}
            </div>
          )}

          <HintsAndGuesses />

          {process.env.REACT_APP_SHOW_SUGGESTIONS && <Suggestions />}
        </div>
      </section>
    </>
  );
};

export default GameContainer;
